import {
  ICellEditorAngularComp,
  ICellRendererAngularComp,
} from 'ag-grid-angular';
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'et-atoms-ag-grid-checkbox-cell',
  imports: [FormsModule],
  template: `
    <input
      [class.pointer-events-none]="!isEditing"
      type="checkbox"
      [(ngModel)]="value"
      (ngModelChange)="onChange($event)"
      class="checkbox no-animation"
    />
  `,
  styles: `:host {@apply flex items-center justify-center h-full w-full;}`,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AgGridCheckboxCellComponent
  implements ICellRendererAngularComp, ICellEditorAngularComp
{
  params: any;
  value = false;
  isEditing = false;

  /**
   * Initializes the cell with the provided parameters.
   *
   * Sets up the cell value and determines if the cell is currently being edited.
   *
   * @param {any} params - The parameters provided by ag-Grid for cell initialization.
   */
  agInit(params: any): void {
    this.params = params;
    const getter = params?.cellRendererParams?.getValue;
    this.value = getter ? getter(params.data) : params.value;
    this.isEditing = !!params.api
      ?.getEditingCells()
      .some(
        (cell: any) =>
          cell.rowIndex === params.rowIndex &&
          cell.column.getId() === params.column.getId(),
      );
  }

  /**
   * Refreshes the cell. Returns false to indicate the cell does not need to be refreshed.
   *
   * @returns {boolean} Always returns false.
   */
  refresh(): boolean {
    return false;
  }

  /**
   * Returns the current value of the cell when editing is complete.
   *
   * @returns {any} The current value of the checkbox cell.
   */
  getValue(): any {
    return this.value;
  }

  /**
   * Handles the click event on the checkbox.
   *
   * Stops the propagation of the click event to prevent unintended grid behavior.
   *
   * @param {MouseEvent} event - The mouse click event.
   */
  onClick(event: MouseEvent): void {
    event.stopPropagation();
  }

  /**
   * Handles the change event for the checkbox value.
   *
   * Calls the setter function (if provided) to update the value
   * in the data model and refreshes the cell.
   *
   * @param {boolean} event - The new value of the checkbox.
   */
  onChange(event: boolean): void {
    const newValue = event;
    const setter = this.params?.colDef?.cellRendererParams?.setValue;
    if (setter) {
      setter(this.params.data, newValue);
      this.params.api.refreshCells({ rowNodes: [this.params.node] });
    }
  }
}
